/**
 * docNoiseFilter — EDX-4 + RCC-7 + DUH shared predicate.
 *
 * Centralises the "is this document noise that shouldn't drown the
 * Documents tab list" predicate. Same code is consumed by:
 *
 *   - `DocumentLibrary` (EDX-4) — hide-noise toggle defaults ON;
 *     the chip header carries "(N hidden)" + "Show all" escape.
 *   - `RerunConfigurationStep` (RCC-7) — rerun-step preflight doc
 *     list applies the same predicate so the user doesn't have to
 *     visually scan z. Archive / .url / 0-byte rows just to decide
 *     whether to rebuild.
 *   - Future Documents_On_Site hardening work (DUH) — same predicate
 *     fed into the upload-time advisory copy.
 *
 * The predicate is intentionally conservative — only filters items
 * that are essentially-never meaningful evidence ("z. Archive/"
 * folder, `.url` / `.eml` shortcuts, 0-byte placeholders, Office
 * temp lock files). Real archive workflows that keep historic
 * letters under different folder names won't be touched.
 *
 * Tenant-specific noise patterns (e.g. "old/" instead of "z. Archive/")
 * are deferred — see EDX-FOLLOWUP-NOISE-PATTERNS.
 */
import type { SiteDocument } from '../types/siteRag';
import { looksLikeParsedEmail } from './emailParser';

/**
 * Returns true when the document should be hidden in default-noise
 * filtering mode. Hosts that call this directly should also surface
 * a one-click toggle to show everything; the predicate must never
 * lock the user out of seeing their own uploads.
 *
 * Patterns:
 *  - Path contains a `z. Archive/` segment (case-insensitive; tolerates
 *    `z.Archive`, `z. archive`, `Z. Archive`).
 *  - File extension is `.url` (Windows shortcut).
 *  - File extension is `.eml` AND the stored text is not a parsed email
 *    with a readable body. Uploads made before the `.eml` parser landed
 *    hold raw MIME source (headers, encoded parts, base64 images) and stay
 *    hidden; header-only messages stay hidden; a parsed email with a body
 *    (a utility's capacity answer, a counterparty's terms) is evidence and
 *    shows like any other document.
 *  - `sizeBytes` is 0 or missing (placeholder rows).
 *  - Filename starts with `~$` (Office temp lock files).
 */
export function isNoisyDoc(d: SiteDocument): boolean {
  const fileName = d.fileName ?? '';
  const folder = d.folder ?? '';
  const path = `${folder}/${fileName}`;
  // z. Archive/ folder anywhere in the path.
  if (/(\/|^)z\.?\s*archive(\/|$)/i.test(path)) return true;
  // Shortcut files are never evidence.
  if (/\.url$/i.test(fileName)) return true;
  // Email files: noise unless the upload parser produced a readable body.
  if (/\.eml$/i.test(fileName)) return !looksLikeParsedEmail(d.textContent);
  // Zero-byte placeholders.
  if (!d.sizeBytes || d.sizeBytes === 0) return true;
  // Office temp-lock files (~$Document.docx). The leaf of the path is
  // what counts since the lock files sit in the same folder as the
  // file they shadow.
  const leaf = fileName.split('/').pop() ?? '';
  if (/^~\$/.test(leaf)) return true;
  return false;
}
